import {
  AVAILABLE_MODEL_VERSIONS,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
  SETTINGS_CONFIG,
  type TASK_SETTING_OPTION,
} from '@/app/constants';
import type { DisplayModelVersion, FormTabType, ModelVersion, TaskInfo } from '@/app/types';

/**
 * 判断任务类型是否为图片转视频类型
 * 包括：img2video、headtailimg2video、multi_frame
 * 这些任务类型在某些场景下具有相似的行为特征
 *
 * @param taskType - 任务类型
 * @returns 如果是图片转视频类型返回 true，否则返回 false
 */
export function isImageToVideoTaskType(taskType: TaskInfo['type'] | string | undefined): boolean {
  return taskType === 'img2video' || taskType === 'headtailimg2video' || taskType === 'multi_frame';
}

/**
 * 图片任务类型
 */
export const IMAGE_TASK_TYPES: readonly TaskInfo['type'][] = [
  'reference2image',
  'text2image',
  'image_edit',
  'upscale_image',
  'style_transfer',
] as const;

/**
 * 音频任务类型
 */
const AUDIO_TASK_TYPES: readonly TaskInfo['type'][] = ['text2audio', 'video2audio', 'tts'] as const;

/**
 * 视频延长任务类型
 */
export const EXTEND_TASK_TYPES: readonly TaskInfo['type'][] = ['video2extend', 'video2extail'] as const;

/**
 * 模版任务类型
 */
export const SCENE_TASK_TYPES: readonly TaskInfo['type'][] = ['scene', 'scene2'] as const;

/**
 * 不支持做同款的任务类型
 */
export const NO_RECREATE_TASK_TYPES: readonly TaskInfo['type'][] = [
  'upscale',
  'upscale_image',
  'music_video',
  'lip_sync',
  'clip_compose',
] as const;

/**
 * 判断是否为音频任务
 */
export const isAudioTask = (taskType?: TaskInfo['type']): boolean => {
  if (!taskType) return false;
  return AUDIO_TASK_TYPES.includes(taskType);
};

/**
 * 判断是否为扩展任务
 */
export const isExtendTask = (taskType?: TaskInfo['type']): boolean => {
  if (!taskType) return false;
  return EXTEND_TASK_TYPES.includes(taskType);
};

/**
 * 判断是否为场景任务
 */
export const isSceneTask = (taskType?: TaskInfo['type']): boolean => {
  if (!taskType) return false;
  return SCENE_TASK_TYPES.includes(taskType);
};

/**
 * 判断是否不支持做同款
 */
export const isNoRecreateTask = (taskType?: TaskInfo['type']): boolean => {
  if (!taskType) return false;
  return NO_RECREATE_TASK_TYPES.includes(taskType);
};

/**
 * 不支持投稿的模型版本列表
 */
export const POST_DISABLED_MODEL_VERSIONS: readonly DisplayModelVersion[] = [
  MODEL_VERSION.Q3_A,
  MODEL_VERSION.Q3_HH,
] as const;

/** admission-notice 活动模板 scene 前缀（如 qinghua-dmission） */
export const ADMISSION_TEMPLATE_SCENE_PREFIX = 'qinghua-' as const;

/**
 * 判断任务是否为 admission-notice 活动模板任务。
 * scene 以 `qinghua-` 开头时视为活动模板，在 mycreations / 模板详情页等位置：禁用投稿、禁用做同款、点击下载直链。
 */
export const isAdmissionTemplateTask = (task?: Pick<TaskInfo, 'scene'> | null): boolean => {
  if (!task?.scene) return false;
  return task.scene.startsWith(ADMISSION_TEMPLATE_SCENE_PREFIX);
};

/**
 * 判断该模型版本是否禁止投稿
 */
export function isPostDisabledModelVersion(modelVersion?: string | null): boolean {
  if (!modelVersion) return false;
  return (POST_DISABLED_MODEL_VERSIONS as readonly string[]).includes(modelVersion);
}

/**
 * 判断是否为图片任务（包括模版类型为图片的情况）
 */
export const isImageTask = (task: Pick<TaskInfo, 'type' | 'extra_params'>): boolean => {
  return IMAGE_TASK_TYPES.includes(task.type) || task.extra_params?.template_type === 'image';
};

/**
 * 将任务类型映射为表单 Tab 类型
 *
 * @param taskType - 任务类型
 * @returns 返回对应的 FormTabType，如果无法映射则返回 null
 */
export function mapTaskTypeToFormTabType(taskType: string | undefined): Exclude<FormTabType, 'scene'> | null {
  if (!taskType) return null;
  if (taskType === 'character2video') return 'character2video';
  if (taskType === 'reference2image') return 'reference2image';
  if (taskType === 'text2image') return 'text2image';
  if (taskType === 'text2video') return 'text2video';
  // 图片转视频相关的类型都映射到 img2video
  if (isImageToVideoTaskType(taskType)) return 'img2video';
  return null;
}

/**
 * video 生成类表单 tab —— 仅这些类型受「模型不在创作页下拉列表则隐藏做同款」限制
 */
const VIDEO_GENERATION_FORM_TABS: readonly Exclude<FormTabType, 'scene'>[] = [
  'text2video',
  'img2video',
  'character2video',
];

/**
 * 判断 video 生成类任务的模型版本是否已不在创作页下拉列表中。
 * 用于决定是否隐藏「做同款」入口：模型已无法在下拉里选到时隐藏入口。
 * 已下线的 1.0/1.5 仍走做同款时强制切到最新模型的降级逻辑，不在此隐藏。
 *
 * @param taskType - 任务类型
 * @param modelVersion - 任务使用的模型版本（settings.model_version）
 * @returns 需要隐藏做同款入口时返回 true
 */
export const isRecreateModelVersionUnavailable = (
  taskType: TaskInfo['type'] | undefined,
  modelVersion: string | undefined,
): boolean => {
  const formTabType = mapTaskTypeToFormTabType(taskType);
  if (!formTabType || !VIDEO_GENERATION_FORM_TABS.includes(formTabType)) return false;
  if (!modelVersion) return false;
  // 1.0/1.5 已下线，做同款时会强制切到最新模型，保留入口
  if (modelVersion === '1.0' || modelVersion === '1.5') return false;
  return !AVAILABLE_MODEL_VERSIONS[formTabType].includes(modelVersion as ModelVersion);
};

/**
 * 检查特定任务设置项在指定模型版本下是否可见
 *
 * @param taskType - 任务类型
 * @param modelVersion - 模型版本
 * @param option - 设置选项，可以是单个选项或选项数组，满足其中一个即可
 * @returns 如果设置项可见则返回 true，否则返回 false
 */
export function isTaskSettingVisible(
  taskType: string | undefined,
  modelVersion: string | undefined,
  option: TASK_SETTING_OPTION | TASK_SETTING_OPTION[],
): boolean {
  const formTabType = mapTaskTypeToFormTabType(taskType);
  if (!formTabType) return false;

  if (!modelVersion) return false;

  const settingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion as ModelVersion];
  if (!settingsKey) return false;

  const settingsConfig = SETTINGS_CONFIG[formTabType][settingsKey];

  if (!settingsConfig) return false;

  if (Array.isArray(option)) {
    return option.some((opt) => settingsConfig.includes(opt));
  }

  return settingsConfig.includes(option);
}
